(() => {
  const STORAGE_KEY = 'teWishlistProducts';

  const HEART_ICON = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 20.6 10.55 19.28C5.4 14.6 2 11.5 2 7.7 2 4.6 4.42 2.2 7.5 2.2c1.74 0 3.41.81 4.5 2.09A6.02 6.02 0 0 1 16.5 2.2C19.58 2.2 22 4.6 22 7.7c0 3.8-3.4 6.9-8.55 11.59L12 20.6Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  const CART_ICON = `
    <svg class="te-product-cart-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 4h2l1.7 9.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 1.9-1.4L20 8H6.1M9.5 19.2a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Zm9 0a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  const readWishlist = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.map(String) : [];
    } catch (error) {
      console.warn('Unable to read TE wishlist.', error);
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.warn('Unable to save TE wishlist.', error);
    }
  };

  const createWishlistButton = (variant, productId) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = variant === 'icon' ? 'te-product-wishlist-icon' : 'te-product-wishlist-text';
    button.dataset.productId = productId;
    button.dataset.wishlistAction = '';
    button.innerHTML = variant === 'icon' ? HEART_ICON : `${HEART_ICON}<span>Add to Wishlist</span>`;
    return button;
  };

  const updateWishlistButtons = (page) => {
    const productId = String(page.dataset.productId || '');
    const isSaved = readWishlist().includes(productId);

    page.querySelectorAll('[data-wishlist-action]').forEach((button) => {
      const label = isSaved ? 'Remove from Wishlist' : 'Add to Wishlist';
      button.setAttribute('aria-pressed', String(isSaved));
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);

      const text = button.querySelector('span');
      if (text) text.textContent = isSaved ? 'Added to Wishlist' : 'Add to Wishlist';
    });
  };

  const toggleWishlist = (page) => {
    const productId = String(page.dataset.productId || '');
    if (!productId) return;

    const wishlist = readWishlist();
    const existingIndex = wishlist.indexOf(productId);
    const saved = existingIndex < 0;

    if (saved) wishlist.push(productId);
    else wishlist.splice(existingIndex, 1);

    writeWishlist(wishlist);
    updateWishlistButtons(page);

    document.dispatchEvent(
      new CustomEvent('te:wishlist-change', {
        detail: { productId, saved, items: wishlist },
      })
    );
  };

  const addCartIcon = (buyButtons) => {
    const textSpan = buyButtons.querySelector('.product-form__submit > span:not(.loading__spinner)');
    if (!textSpan || textSpan.querySelector('.te-product-cart-icon')) return;
    textSpan.insertAdjacentHTML('afterbegin', CART_ICON);
  };

  const bindPurchaseEvents = (purchase, page) => {
    if (purchase.dataset.eventsBound === 'true') return;

    purchase.dataset.eventsBound = 'true';
    purchase.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist-action]');
      if (!button) return;
      event.preventDefault();
      toggleWishlist(page);
    });
  };

  const normalizeExistingPurchase = (purchase, page) => {
    const primary = purchase.querySelector('.te-product-purchase__primary');
    const secondary = purchase.querySelector('.te-product-purchase__secondary');
    if (!primary || !secondary) return false;

    const quantity = purchase.querySelector('.product-form__quantity');
    const buyButtons = purchase.querySelector('.te-product-buy-buttons');
    const iconWishlist = purchase.querySelector('.te-product-wishlist-icon');
    const textWishlist = purchase.querySelector('.te-product-wishlist-text');
    const share = purchase.querySelector('.share-button, share-button');

    if (!quantity || !buyButtons || !iconWishlist) return false;

    /* Remove the old spacer/column structure and rebuild one clean grid row. */
    primary.replaceChildren(quantity, buyButtons, iconWishlist);
    secondary.replaceChildren();
    if (textWishlist) secondary.append(textWishlist);
    if (share) secondary.append(share);

    purchase.querySelectorAll('.te-product-purchase__label-spacer, .te-product-purchase__column').forEach((item) => {
      if (!item.contains(quantity) && !item.contains(buyButtons) && !item.contains(iconWishlist)) item.remove();
    });

    addCartIcon(buyButtons);
    bindPurchaseEvents(purchase, page);
    updateWishlistButtons(page);
    return true;
  };

  const initializePage = (page) => {
    const info = page.querySelector('.product__info-container');
    if (!info) return;

    const existingPurchase = info.querySelector('.te-product-purchase');
    if (existingPurchase && normalizeExistingPurchase(existingPurchase, page)) return;

    const quantity = info.querySelector('.product-form__quantity');
    const buyButtons = info.querySelector('.te-product-buy-buttons');
    const share = info.querySelector('.share-button, share-button');
    if (!quantity || !buyButtons || !quantity.parentNode) return;

    const insertionParent = quantity.parentNode;
    const insertionReference = quantity;
    const productId = String(page.dataset.productId || '');

    const purchase = document.createElement('div');
    purchase.className = 'te-product-purchase';

    const primary = document.createElement('div');
    primary.className = 'te-product-purchase__primary';

    const secondary = document.createElement('div');
    secondary.className = 'te-product-purchase__secondary';

    const iconWishlist = createWishlistButton('icon', productId);
    const textWishlist = createWishlistButton('text', productId);

    insertionParent.insertBefore(purchase, insertionReference);
    purchase.append(primary, secondary);

    /* Direct grid children: quantity, Add to Cart, heart. No fake label spacers. */
    primary.append(quantity, buyButtons, iconWishlist);
    secondary.append(textWishlist);
    if (share) secondary.append(share);

    addCartIcon(buyButtons);
    bindPurchaseEvents(purchase, page);
    updateWishlistButtons(page);
  };

  let scheduled = false;

  const initializeAll = () => {
    scheduled = false;
    document.querySelectorAll('product-info.te-product-page').forEach((page) => {
      try {
        initializePage(page);
      } catch (error) {
        console.error('Unable to initialize TE product actions.', error);
      }
    });
  };

  const scheduleInitialize = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(initializeAll);
  };

  document.addEventListener('DOMContentLoaded', scheduleInitialize);
  document.addEventListener('product-info:loaded', scheduleInitialize);
  document.addEventListener('shopify:section:load', scheduleInitialize);

  if (document.readyState !== 'loading') scheduleInitialize();
})();
